import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TAGS = ["academic", "family", "friendship", "loneliness", "grief", "hope", "relationships", "identity"];

export default function Write() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("academic");
  const [anonymous, setAnonymous] = useState(true);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/stories", { title, body, tag, anonymous });
      toast.success("Your story is out there.");
      navigate(`/story/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not publish.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-0 py-20" data-testid="page-write">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
        <span className="editorial-rule" />Write a story
      </div>
      <h1 className="font-serif text-5xl text-mindora-ink leading-tight">
        Say what you've been keeping <em className="italic text-mindora-red">quiet.</em>
      </h1>
      <p className="mt-4 text-mindora-muted leading-relaxed">
        Choose anonymity if you need it. You can always change your mind before publishing.
      </p>

      <form onSubmit={handle} className="mt-10 space-y-7" data-testid="write-form">
        <div>
          <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            data-testid="write-title-input"
            placeholder="In one line — what is this about?"
            className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red text-lg font-serif h-12"
          />
        </div>

        <div>
          <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Tag</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="write-tags">
            {TAGS.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setTag(t)}
                data-testid={`write-tag-${t}`}
                className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-all ${
                  tag === t
                    ? "bg-mindora-red text-mindora-bg border-mindora-red"
                    : "border-mindora-line text-mindora-ink hover:border-mindora-red"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Your story</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={14}
            data-testid="write-body-input"
            placeholder="Write the truth. Not the polished version."
            className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-lg leading-relaxed"
          />
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-mindora-line bg-mindora-panel p-5">
          <div>
            <div className="text-mindora-ink font-medium">Publish anonymously</div>
            <div className="text-xs text-mindora-muted mt-0.5">Your name will not appear. Even on Mindora staff dashboards.</div>
          </div>
          <Switch
            checked={anonymous}
            onCheckedChange={setAnonymous}
            data-testid="write-anonymous-switch"
            className="data-[state=checked]:bg-mindora-red"
          />
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="write-submit-button"
          className="btn-primary rounded-full px-8 py-3 h-auto text-sm tracking-wide"
        >
          {busy ? "Publishing…" : "Publish"}
        </Button>
      </form>
    </div>
  );
}
